import React from 'react'
import { useTranslation } from 'react-i18next'

import { FormikProps } from 'formik'
import { Button } from 'primereact/button'
import { Calendar } from 'primereact/calendar'
import { Dialog } from 'primereact/dialog'
import { Divider } from 'primereact/divider'
import { InputNumber } from 'primereact/inputnumber'
import { InputText } from 'primereact/inputtext'
import { classNames } from 'primereact/utils'

import { DateTemplate } from '../../../../../../../../components/DateTemplate'
import { StatusTag } from '../../../../../../../../components/tableTemplates'
import { DeliveryForecastItemSplit } from '../../../../../../../../data/models'
import { calculateBreakpointWidth } from '../../../../../../../../utils/helper/calculateBreakpointWidth'
import { ConfigurableComponent } from '../../../../../../../../utils/wrapper'
import { useConfigContext } from '../../../../../../../ui-config'
import { useDeliveryForecastItemById } from '../../../../../../data'
import { FormFieldName, FormValues } from './EditSplitItem.container'

interface Props {
  deliveryForecastId: string
  visible: boolean
  setVisible: (params: boolean) => void
  itemId: string
  formik: FormikProps<any>
  isFormFieldValid: (name: FormFieldName) => boolean
  getConfirmationCode: (
    formData: FormValues,
  ) => DeliveryForecastItemSplit['Status']
  getFormErrorMessage: (name: FormFieldName) => false | JSX.Element
  handleClose: () => void
  handleReset: () => void
  isRequestRunning: boolean
  rowData: any
}

export const EditSplitItemView: React.FC<Props> = ({
  deliveryForecastId,
  visible,
  setVisible,
  itemId,
  formik,
  isFormFieldValid,
  getConfirmationCode,
  getFormErrorMessage,
  handleClose,
  handleReset,
  isRequestRunning,
  rowData,
}) => {
  const { t, i18n } = useTranslation()
  const { language } = i18n
  const { uiConfig: config } = useConfigContext()

  const { data, isLoading } = useDeliveryForecastItemById(
    deliveryForecastId,
    itemId,
  )

  // ************************************************************
  // components
  // ************************************************************
  // buyerProductId
  const buyerProductIdLabel = (
    <label htmlFor="buyerProductId" className="text-sm"></label>
  )

  const buyerProductIdComponent = (
    <InputText
      id="buyerProductId"
      className="w-full"
      value={data?.BuyerProductId}
      disabled
    />
  )

  const buyerProductIdWrapper = (
    <ConfigurableComponent
      config={config.deliveryForecast.editSplitItem}
      component={buyerProductIdComponent}
      label={buyerProductIdLabel}
      isLoading={isLoading}
    />
  )

  // confirmationProductId
  const confirmationProductIdLabel = (
    <label htmlFor="confirmationProductId" className="text-sm"></label>
  )

  const confirmationProductIdComponent = (
    <InputText
      id="confirmationProductId"
      className="w-full"
      value={data?.ConfirmationProductId}
      disabled
    />
  )

  const confirmationProductIdWrapper = (
    <ConfigurableComponent
      config={config.deliveryForecast.editSplitItem}
      component={confirmationProductIdComponent}
      label={confirmationProductIdLabel}
      isLoading={isLoading}
    />
  )

  // buyerProductDescription
  const buyerProductDescriptionLabel = (
    <label htmlFor="buyerProductDescription" className="text-sm"></label>
  )

  const buyerProductDescriptionComponent = (
    <InputText
      id="buyerProductDescription"
      className="w-full"
      value={data?.BuyerProductDescription}
      disabled
    />
  )

  const buyerProductDescriptionWrapper = (
    <ConfigurableComponent
      config={config.deliveryForecast.editSplitItem}
      component={buyerProductDescriptionComponent}
      label={buyerProductDescriptionLabel}
      isLoading={isLoading}
    />
  )

  // buyerDeliveryDate
  const buyerDeliveryDateLabel = (
    <label htmlFor="buyerDeliveryDate" className="text-sm"></label>
  )

  const buyerDeliveryDateComponent = (
    <Calendar
      id="buyerDeliveryDate"
      value={
        rowData.BuyerDeliveryDate
          ? new Date(rowData.BuyerDeliveryDate)
          : undefined
      }
      className="w-full"
      showIcon
      locale={language}
      disabled
    />
  )
  const buyerDeliveryDateWrapper = (
    <ConfigurableComponent
      config={config.deliveryForecast.editSplitItem}
      component={buyerDeliveryDateComponent}
      label={buyerDeliveryDateLabel}
      isLoading={isLoading}
    />
  )

  // confirmationDeliveryDate
  const confirmationDeliveryDateLabel = (
    <label
      htmlFor="confirmationDeliveryDate"
      className={classNames('text-sm', {
        'p-error': !isFormFieldValid('confirmationDeliveryDate'),
      })}
    ></label>
  )

  const confirmationDeliveryDateComponent = (
    <Calendar
      id="confirmationDeliveryDate"
      value={new Date(formik.values.confirmationDeliveryDate)}
      onChange={formik.handleChange}
      className={classNames('w-full', {
        'p-invalid': !isFormFieldValid('confirmationDeliveryDate'),
      })}
      showButtonBar
      showIcon
      minDate={new Date()}
      dateTemplate={(date: any) => (
        <DateTemplate
          date={date}
          // desiredDeliveryDate={data?.ConfirmationDeliveryDate}
        />
      )}
      locale={language}
      disabled
    />
  )

  const confirmationDeliveryDateErrorMsg = (
    <div>{getFormErrorMessage('confirmationDeliveryDate')}</div>
  )

  const confirmationDeliveryDateWrapper = (
    <ConfigurableComponent
      config={config.deliveryForecast.editSplitItem}
      component={confirmationDeliveryDateComponent}
      label={confirmationDeliveryDateLabel}
      errorMsg={confirmationDeliveryDateErrorMsg}
      isLoading={isLoading}
    />
  )

  //  buyerDeliveryQuantity
  const buyerDeliveryQuantityLabel = (
    <label htmlFor="buyerDeliveryQuantity" className="text-sm"></label>
  )

  const buyerDeliveryQuantityComponent = (
    <InputNumber
      id="buyerDeliveryQuantity"
      className="w-full"
      value={rowData.BuyerDeliveryQuantity}
      locale={language}
      disabled
    />
  )

  const buyerDeliveryQuantityWrapper = (
    <ConfigurableComponent
      config={config.deliveryForecast.editSplitItem}
      component={buyerDeliveryQuantityComponent}
      label={buyerDeliveryQuantityLabel}
      isLoading={isLoading}
    />
  )

  // confirmationDeliveryQuantity
  const confirmationDeliveryQuantityLabel = (
    <label
      htmlFor="confirmationDeliveryQuantity"
      className={classNames('text-sm', {
        'p-error': !isFormFieldValid('confirmationDeliveryQuantity'),
      })}
    ></label>
  )

  const confirmationDeliveryQuantityComponent = (
    <InputNumber
      id="confirmationDeliveryQuantity"
      value={formik.values.confirmationDeliveryQuantity}
      onChange={event =>
        formik.setFieldValue('confirmationDeliveryQuantity', event.value)
      }
      className={classNames('w-full', {
        'p-invalid': !isFormFieldValid('confirmationDeliveryQuantity'),
      })}
      locale={language}
      disabled={getConfirmationCode(formik.values) === 'REJECTED'}
    />
  )

  const confirmationDeliveryQuantityErrorMsg = (
    <div>{getFormErrorMessage('confirmationDeliveryQuantity')}</div>
  )

  const confirmationDeliveryQuantityWrapper = (
    <ConfigurableComponent
      config={config.deliveryForecast.editSplitItem}
      component={confirmationDeliveryQuantityComponent}
      label={confirmationDeliveryQuantityLabel}
      errorMsg={confirmationDeliveryQuantityErrorMsg}
      isLoading={isLoading}
    />
  )

  // ************************************************************
  // dialog components
  // ************************************************************
  // header
  const header = (
    <>
      <div className="flex align-content-center flex-wrap">
        <span className="mr-3">
          {t(
            'deliveryForecast.deliveryScheduleDetails.splits.table.edit.dialog.title',
          )}
        </span>

        <StatusTag status={getConfirmationCode(formik.values)} />
      </div>
    </>
  )

  // footer
  const footer = (
    <div className="flex flex-wrap justify-content-between pt-3">
      {rowData.Status !== 'ADDED' && (
        <Button
          className="p-button-text w-full md:w-auto"
          label={t('common.buttons.reset')}
          icon="pi pi-replay"
          onClick={handleReset}
          type="reset"
          disabled={
            !(
              getConfirmationCode(formik.values) === 'MODIFIED' ||
              getConfirmationCode(formik.values) === 'REJECTED'
            )
          }
        />
      )}

      <Button
        className="p-button-text ml-auto w-full md:w-auto mb-1 md:mb-0"
        label={t('common.buttons.cancel')}
        icon="pi pi-times"
        onClick={handleClose}
        type="button"
      />

      <Button
        label={t('common.buttons.save')}
        className="w-full md:w-auto"
        icon={'pi pi-check'}
        type="submit"
        onClick={formik.submitForm}
        loading={isRequestRunning}
        disabled={!formik.dirty}
      />
    </div>
  )

  return (
    <>
      <Button
        icon="pi pi-pencil"
        className="p-button-rounded p-button-text"
        onClick={() => setVisible(true)}
        visible={config.deliveryForecast.editSplitItem.enabled !== false}
      />

      <Dialog
        header={header}
        visible={visible}
        style={{ width: '55rem' }}
        breakpoints={{ [calculateBreakpointWidth(55)]: '90vw' }}
        footer={footer}
        onHide={handleClose}
        blockScroll
      >
        {/* buyerProductId */}
        <div className="formgrid grid">
          <div className="col-12 md:col-6">{buyerProductIdWrapper}</div>
          <div className="col-12 md:col-6">{confirmationProductIdWrapper}</div>
        </div>

        <Divider className="md:hidden" />

        {/* buyerProductDescription */}
        <div className="formgrid grid">
          <div className="col-12 md:col-6">
            {buyerProductDescriptionWrapper}
          </div>
        </div>

        <Divider className="md:hidden" />

        {/* buyerDeliveryDate  ausschalten beim Editieren*/}
        <div className="formgrid grid">
          <div className="col-12 md:col-6">{buyerDeliveryDateWrapper}</div>
          <div className="col-12 md:col-6">
            {confirmationDeliveryDateWrapper}
          </div>
        </div>

        <Divider className="md:hidden" />

        {/* buyerDeliveryQuantity */}
        <div className="formgrid grid">
          <div className="col-12 md:col-6">{buyerDeliveryQuantityWrapper}</div>
          <div className="col-12 md:col-6">
            {confirmationDeliveryQuantityWrapper}
          </div>
        </div>
      </Dialog>
    </>
  )
}
